import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SHIPPING_DEFAULTS } from "@/lib/shipping";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function Shipping() {
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/shipping/settings")
      .then((r) => { if (alive && r.data) setPolicy({ ...SHIPPING_DEFAULTS, ...r.data }); })
      .catch(() => { if (alive) setPolicy(SHIPPING_DEFAULTS); });
    return () => { alive = false; };
  }, []);

  const p = policy || SHIPPING_DEFAULTS;
  const price = Number(p.base_price || 0);
  const priceText = price === 0 ? "free" : `$${price.toFixed(2)}`;
  const costLine = price === 0
    ? "Standard shipping is free on all orders."
    : p.mode === "flat"
      ? `Standard shipping is a flat ${priceText} per order, regardless of how many books you buy.`
      : `Standard shipping is ${priceText} per book and is calculated automatically in your cart and at checkout.`;

  const sections = [
    {
      h: "Order Processing",
      b: [
        `All orders are processed within ${plural(p.handling_days, "business day")} after payment has been successfully received and verified.`,
        "Orders are carefully prepared for shipment before dispatch.",
        "Processing times may be longer during holidays, peak shopping periods, or due to circumstances beyond our control.",
      ],
    },
    {
      h: "Shipping",
      b: [
        "We currently ship to customers within the United States.",
        costLine,
        `Estimated delivery time is ${p.delivery_min_days === p.delivery_max_days ? plural(p.delivery_max_days, "business day") : `${p.delivery_min_days}–${p.delivery_max_days} business days`} after the order has been shipped.`,
        "Some titles carry their own shipping price or delivery estimate — when they do, that estimate is shown on the book's page and in your cart.",
        "Digital products (eBooks and audiobooks) are delivered electronically and never incur shipping charges.",
        "Delivery times may vary depending on destination, carrier performance, weather conditions, holidays, and other unforeseen circumstances.",
        "Delivery estimates are not guaranteed.",
      ],
    },
    {
      h: "Order Tracking",
      b: [
        "Once your order has been shipped, a shipping confirmation email containing tracking information will be sent to the email address used when placing your order.",
        "Customers can use the tracking number to monitor shipment progress.",
      ],
    },
    {
      h: "Shipping Delays",
      b: [
        "Although we strive to meet estimated delivery times, occasional delays may occur.",
        "If your shipment has not arrived within a reasonable time after the estimated delivery window, please contact our customer support team and we will assist you in investigating the shipment.",
      ],
    },
    {
      h: "Order Cancellations",
      b: [
        "Orders may be cancelled before they have entered the shipping process.",
        "Once an order has been shipped, it can no longer be cancelled.",
        "If you no longer require the item after shipment, please refer to our Return & Refund Policy.",
      ],
    },
  ];

  const updated = p.updated_at
    ? new Date(p.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "December 2025";

  return (
    <div className="pt-28 md:pt-32 pb-20 bg-[#F9F6F0]" data-testid="shipping-page">
      <div className="max-w-[800px] mx-auto px-6">
        <h1
          className="text-3xl md:text-4xl font-semibold text-slate-900 text-center mb-3"
          data-testid="shipping-title"
        >
          Shipping &amp; Cancellations
        </h1>
        <p
          className="text-sm text-slate-500 text-center mb-10"
          data-testid="shipping-eyebrow"
        >
          Last Updated: {updated}
        </p>

        <div className="space-y-6 text-base leading-relaxed text-slate-700" data-testid="shipping-live-policy">
          {sections.map((s, idx) => (
            <section key={s.h} data-testid={`shipping-section-${String(idx + 1).padStart(2, "0")}`}>
              <h2 className="text-xl font-medium mt-8 mb-3 text-slate-900">{s.h}</h2>
              <div className="space-y-3">
                {s.b.map((par, i) => <p key={i}>{par}</p>)}
              </div>
              {s.h === "Order Cancellations" && (
                <p className="mt-3">
                  <Link
                    to="/returns"
                    data-testid="shipping-return-link"
                    className="text-slate-900 underline hover:text-[#7A2021]"
                  >
                    Read the Return &amp; Refund Policy →
                  </Link>
                </p>
              )}
            </section>
          ))}

          <section data-testid="shipping-section-support">
            <h2 className="text-xl font-medium mt-8 mb-3 text-slate-900">Customer Support</h2>
            <p>
              If you have any questions regarding shipping, tracking, or order cancellations, please contact:
            </p>
            <div className="mt-3">
              <p className="font-medium text-slate-900">Blessings Connection LLC</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:customerservice@blessingsconnection.com"
                  className="text-slate-900 underline hover:text-[#7A2021]"
                  data-testid="shipping-contact-email"
                >
                  customerservice@blessingsconnection.com
                </a>
              </p>
              <p className="mt-2">Our customer support team aims to respond within 1–2 business days.</p>
            </div>
          </section>

          <div className="border-t border-slate-200 pt-6 mt-10">
            <p className="text-sm text-slate-500">
              Blessings Connection LLC · blessingsconnection.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
